/** 原子件：密集表格。
 *
 * 形状对齐原型 `ui.js` 的 `table(cols, rows, opts)`：列定义带 `num` / `mono` / `wrap` / `w`，
 * 行带 `cls` / `selected` / `onClick`，空数据渲染成整格的空态占位。
 * 密集操作台的取向是「一屏能扫完」，所以不引入任何表格库。
 */

import type { ReactNode } from "react";

export interface Column {
  label: ReactNode;
  /** 右对齐 + 等宽，数字列必须用。 */
  num?: boolean;
  mono?: boolean;
  wrap?: boolean;
  width?: string;
}

export interface Row {
  key: string;
  cells: ReactNode[];
  cls?: string;
  selected?: boolean;
  onClick?: () => void;
}

export interface TableProps {
  cols: Column[];
  rows: Row[];
  /** 空数据时的文案；给了它就说明「没有数据」是一个正常结论。 */
  empty?: ReactNode;
  tall?: boolean;
  scroll?: boolean;
}

export function Table({ cols, rows, empty = "无记录", tall, scroll = true }: TableProps): ReactNode {
  const body =
    rows.length === 0 ? (
      <tr>
        <td colSpan={cols.length} className="emptycell">
          <div className="empty">{empty}</div>
        </td>
      </tr>
    ) : (
      rows.map((row) => (
        <tr
          key={row.key}
          className={[row.cls ?? "", row.selected ? "sel" : ""].filter(Boolean).join(" ") || undefined}
          onClick={row.onClick}
          style={row.onClick ? { cursor: "pointer" } : undefined}
        >
          {row.cells.map((cell, index) => {
            const col = cols[index];
            const cls = [col?.num ? "num" : "", col?.mono ? "mono" : "", col?.wrap ? "wrap" : ""]
              .filter(Boolean)
              .join(" ");
            return (
              <td key={index} className={cls || undefined}>
                {cell}
              </td>
            );
          })}
        </tr>
      ))
    );

  const table = (
    <table className="t">
      <thead>
        <tr>
          {cols.map((col, index) => (
            <th
              key={index}
              className={[col.num ? "num" : "", col.mono ? "mono" : ""].filter(Boolean).join(" ") || undefined}
              style={col.width ? { width: col.width } : undefined}
            >
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{body}</tbody>
    </table>
  );

  if (!scroll) return table;
  return <div className={tall ? "tablescroll tall" : "tablescroll"}>{table}</div>;
}
